import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import { Plus, Trash2, Check, Clock, Users, Copy, Mail } from "lucide-react";
import { toast } from "sonner";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";

const CATS = [
  { id: "n_plus_1", fr: "N+1 (supérieur)", en: "N+1 (manager)", color: "#0F3556" },
  { id: "n", fr: "Pair (N)", en: "Peer (N)", color: "#EA5A0C" },
  { id: "n_minus_1", fr: "N-1 (équipe)", en: "N-1 (team)", color: "#7C9A92" },
  { id: "other", fr: "Autre", en: "Other", color: "#B58A4A" },
];

const SELF = { id: "self", fr: "Auto-évaluation", en: "Self-assessment", color: "#1F2937" };

export default function Raters360({ assignmentId, scope = "me", showResults = false, ownerLabel = null }) {
  const { lang } = useLang();
  const [raters, setRaters] = useState([]);
  const [results, setResults] = useState(null);
  const [form, setForm] = useState({ name: "", first_name: "", email: "", category: "n_plus_1", gender: "" });
  const [adding, setAdding] = useState(false);

  const base = scope === "admin" ? `/admin/360/${assignmentId}` : `/me/tests/${assignmentId}`;
  const ratersPath = scope === "admin" ? `${base}/raters` : `${base}/raters`;
  const resultsPath = scope === "admin" ? `${base}/results` : `${base}/360-results`;
  const invitePath = scope === "admin" ? `${base}/invite-rater` : `${base}/invite-rater`;

  const load = async () => {
    try {
      const { data } = await api.get(ratersPath);
      setRaters(data);
      if (showResults) {
        const { data: r } = await api.get(resultsPath);
        setResults(r);
      }
    } catch (err) {
      // 404 / 403 are expected before any raters are invited; only surface unexpected ones
      if (err?.response?.status !== 404 && err?.response?.status !== 403) {
        console.error("Raters360: load failed", err);
      }
    }
  };
  useEffect(() => { load(); }, [assignmentId]);

  const invite = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(invitePath, form);
      if (data.email_delivered) {
        toast.success(lang === "fr" ? "Invitation envoyée par email" : "Invitation sent by email");
      } else {
        toast.warning(lang === "fr" ? "Email non remis — copiez le lien d'invitation" : "Email not delivered — copy the invitation link");
      }
      setForm({ name: "", first_name: "", email: "", category: "n_plus_1", gender: "" });
      setAdding(false);
      load();
    } catch (e2) { toast.error(formatApiError(e2)); }
  };

  const remove = async (id) => {
    const path = scope === "admin" ? `/admin/360/${assignmentId}/raters/${id}` : `/me/tests/${assignmentId}/raters/${id}`;
    await api.delete(path);
    load();
  };

  const copyLink = (url) => {
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => {
      toast.success(lang === "fr" ? "Lien copié" : "Link copied");
    });
  };

  const resend = async (id) => {
    try {
      const path = scope === "admin" ? `/admin/360/${assignmentId}/raters/${id}/resend` : `/me/tests/${assignmentId}/raters/${id}/resend`;
      const { data } = await api.post(path);
      if (data.email_delivered) toast.success(lang === "fr" ? "Email renvoyé" : "Email resent");
      else toast.warning(lang === "fr" ? "Email non remis" : "Email not delivered");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const maxScale = results?.template?.scale?.max || 5;
  const dimsOrder = results ? Object.keys(results.template.dimensions || {}) : [];

  // Build radar dataset: each axis is a dimension; each series is a category
  const radarData = dimsOrder.map((dimId) => {
    const meta = results?.template?.dimensions?.[dimId] || {};
    const row = { dimension: lang === "fr" ? meta.fr || dimId : meta.en || dimId };
    [SELF, ...CATS].forEach((cat) => {
      const agg = results?.aggregated?.[cat.id];
      const d = agg?.dimensions?.find((x) => x.id === dimId);
      if (d) row[cat.id] = d.average;
    });
    return row;
  });

  const activeCats = [SELF, ...CATS].filter((c) => (results?.aggregated?.[c.id]?.raters_count || 0) > 0);

  return (
    <div data-testid="raters-360-panel" className="mt-12 bg-white border border-black/10 p-8 md:p-10">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand mb-2">— {lang === "fr" ? "Évaluation 360°" : "360° Evaluation"}</div>
          <h3 className="font-serif text-2xl text-navy font-light">{lang === "fr" ? "Évaluateurs invités" : "Invited raters"}</h3>
        </div>
        {raters.length < 10 && (
          <button data-testid="add-rater" onClick={() => setAdding(true)} className="inline-flex items-center gap-2 bg-brand hover:bg-brandDark text-white px-4 py-2 text-[10px] uppercase tracking-[0.18em] font-mono">
            <Plus size={12} /> {lang === "fr" ? "Inviter" : "Invite"}
          </button>
        )}
      </div>

      {adding && (
        <form onSubmit={invite} data-testid="invite-rater-form" className="grid grid-cols-12 gap-4 mb-6 bg-ivory p-5 border border-black/10">
          <select data-testid="rater-gender" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} className="col-span-12 md:col-span-2 bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand text-sm">
            <option value="">{lang === "fr" ? "Genre…" : "Title…"}</option>
            <option value="madame">{lang === "fr" ? "Madame" : "Ms."}</option>
            <option value="monsieur">{lang === "fr" ? "Monsieur" : "Mr."}</option>
          </select>
          <input data-testid="rater-first-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder={lang === "fr" ? "Prénom" : "First name"} className="col-span-12 md:col-span-2 bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand text-sm" />
          <input data-testid="rater-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={lang === "fr" ? "Nom" : "Name"} className="col-span-12 md:col-span-2 bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand text-sm" />
          <input data-testid="rater-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@…" className="col-span-12 md:col-span-3 bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand text-sm" />
          <select data-testid="rater-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="col-span-12 md:col-span-2 bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand text-sm">
            {CATS.map((c) => <option key={c.id} value={c.id}>{lang === "fr" ? c.fr : c.en}</option>)}
          </select>
          <button data-testid="invite-submit" type="submit" className="col-span-12 md:col-span-1 bg-brand text-white text-[10px] uppercase tracking-[0.18em] font-mono py-2">OK</button>
        </form>
      )}

      <div className="border border-black/10 divide-y divide-black/10">
        {raters.length === 0 && <div className="p-4 text-sm text-ink/50">{lang === "fr" ? "Aucun évaluateur invité" : "No rater invited"}</div>}
        {raters.map((r) => (
          <div key={r.id} className="p-3 px-4 flex items-center justify-between text-sm gap-2 flex-wrap" data-testid={`rater-row-${r.id}`}>
            <div className="flex items-center gap-3 min-w-0">
              {r.status === "completed" ? <Check size={14} className="text-brand flex-none" /> : <Clock size={14} className="text-ink/40 flex-none" />}
              <span className="text-navy font-medium truncate">
                {r.gender === "madame" ? "Mme " : (r.gender === "monsieur" ? "M. " : "")}{r.first_name ? `${r.first_name} ` : ""}{r.name}
              </span>
              <span className="text-ink/50 text-xs hidden md:inline">· {r.email}</span>
            </div>
            <div className="flex items-center gap-3 flex-none">
              <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-ink/55">{CATS.find((c) => c.id === r.category)?.[lang]}</span>
              {r.status !== "completed" && (
                <>
                  <button
                    data-testid={`copy-link-${r.id}`}
                    onClick={() => copyLink(r.invitation_url)}
                    title={lang === "fr" ? "Copier le lien d'invitation" : "Copy invitation link"}
                    className="text-ink/50 hover:text-brand inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-mono"
                  >
                    <Copy size={12} /> {lang === "fr" ? "Lien" : "Link"}
                  </button>
                  <button
                    data-testid={`resend-${r.id}`}
                    onClick={() => resend(r.id)}
                    title={lang === "fr" ? "Renvoyer l'email" : "Resend email"}
                    className="text-ink/50 hover:text-brand inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-mono"
                  >
                    <Mail size={12} /> {lang === "fr" ? "Renvoyer" : "Resend"}
                  </button>
                </>
              )}
              <button onClick={() => remove(r.id)} className="text-ink/40 hover:text-destructive"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {/* Aggregated radar — admin only (showResults) */}
      {showResults && results && activeCats.length > 0 && (
        <div className="mt-10 pt-8 border-t border-black/10">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand mb-6">— {lang === "fr" ? "Radar par compétence et catégorie" : "Radar by competency and category"}</div>

          <div className="bg-ivory p-4 md:p-6 border border-black/10" data-testid="radar-360">
            <div style={{ width: "100%", height: 460 }}>
              <ResponsiveContainer>
                <RadarChart data={radarData} margin={{ top: 24, right: 40, bottom: 24, left: 40 }}>
                  <PolarGrid stroke="#0F3556" strokeOpacity={0.18} />
                  <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 12, fill: "#0F3556" }} />
                  <PolarRadiusAxis angle={90} domain={[0, maxScale]} tick={{ fontSize: 10, fill: "#0F3556", fillOpacity: 0.5 }} />
                  {activeCats.map((cat) => (
                    <Radar
                      key={cat.id}
                      name={lang === "fr" ? cat.fr : cat.en}
                      dataKey={cat.id}
                      stroke={cat.color}
                      fill={cat.color}
                      fillOpacity={0.18}
                      strokeWidth={2}
                    />
                  ))}
                  <Legend wrapperStyle={{ fontSize: 11, paddingTop: 16, fontFamily: "monospace", letterSpacing: "0.12em", textTransform: "uppercase" }} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid rgba(0,0,0,0.1)", fontSize: 12 }} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Per-dimension comparison */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {dimsOrder.map((dimId) => {
              const meta = results.template.dimensions?.[dimId] || {};
              const label = lang === "fr" ? meta.fr || dimId : meta.en || dimId;
              return (
                <div key={dimId} className="bg-white border border-black/10 p-5" data-testid={`dim-card-${dimId}`}>
                  <div className="font-serif text-lg text-navy mb-3">{label}</div>
                  <div className="space-y-2.5">
                    {activeCats.map((cat) => {
                      const agg = results.aggregated[cat.id];
                      const d = agg?.dimensions?.find((x) => x.id === dimId);
                      if (!d) return null;
                      return (
                        <div key={cat.id}>
                          <div className="flex items-baseline justify-between text-xs mb-1">
                            <span className="text-ink/70 truncate mr-3" style={{ color: cat.color }}>
                              {lang === "fr" ? cat.fr : cat.en}
                            </span>
                            <span className="num-display text-sm text-navy flex-none">
                              {d.average}<span className="text-ink/45 text-[10px]"> / {maxScale}</span>
                            </span>
                          </div>
                          <div className="h-1 bg-black/5">
                            <div className="h-full transition-all duration-700" style={{ width: `${d.percentage}%`, background: cat.color }}></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {results.verbatim_synthesis && (
            <div className="mt-10 pt-8 border-t border-black/10" data-testid="verbatim-synth">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand">— Verbatim — synthèse</div>
                {scope === "admin" && (
                  <div className="inline-flex items-center gap-2 flex-wrap">
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/360/${assignmentId}/pdf?lang=fr`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="download-360-pdf"
                      className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-mono border border-black/15 hover:border-brand hover:text-brand px-3 py-2"
                    >
                      Synthèse 360° (PDF FR)
                    </a>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/360/${assignmentId}/pdf?lang=en`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="download-360-pdf-en"
                      className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-mono border border-black/15 hover:border-brand hover:text-brand px-3 py-2"
                    >
                      360° Synthesis (PDF EN)
                    </a>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/360/${assignmentId}/results-pdf`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="download-360-results-pdf"
                      className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-mono border border-black/15 hover:border-brand hover:text-brand px-3 py-2"
                    >
                      {lang === "fr" ? "Résultats bruts (PDF)" : "Raw results (PDF)"}
                    </a>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand mt-8 mb-3">— {lang === "fr" ? "Verbatims bruts par évaluateur" : "Raw verbatims per rater"}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-emerald-50 border border-emerald-200 p-5" data-testid="synth-strengths">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-800 mb-3">{lang === "fr" ? "Forces" : "Strengths"}</div>
                  {results.verbatim_synthesis.strengths.length === 0 && <p className="text-xs italic text-ink/50">{lang === "fr" ? "Aucun commentaire renseigné." : "No comment provided."}</p>}
                  <ul className="space-y-3">
                    {results.verbatim_synthesis.strengths.map((s, i) => (
                      <li key={i} className="text-sm text-ink/85">
                        <span className="italic">« {s.text} »</span>
                        <div className="text-[9px] uppercase tracking-[0.2em] font-mono text-emerald-700/80 mt-1">{s.level_label_fr}</div>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-amber-50 border border-amber-200 p-5" data-testid="synth-improvements">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-900 mb-3">{lang === "fr" ? "Axes de progrès" : "Areas to improve"}</div>
                  {results.verbatim_synthesis.improvements.length === 0 && <p className="text-xs italic text-ink/50">{lang === "fr" ? "Aucun commentaire renseigné." : "No comment provided."}</p>}
                  <ul className="space-y-3">
                    {results.verbatim_synthesis.improvements.map((s, i) => (
                      <li key={i} className="text-sm text-ink/85">
                        <span className="italic">« {s.text} »</span>
                        <div className="text-[9px] uppercase tracking-[0.2em] font-mono text-amber-800/80 mt-1">{s.level_label_fr}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
